import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Users, ShieldCheck, Crown, Verified, Phone } from "lucide-react";
import api, { inrFormat, categoryLabel, apiErrorMessage, productUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import BhegaBadge from "../components/BhegaBadge";
import CountdownTimer from "../components/CountdownTimer";
import CommentThread from "../components/CommentThread";
import ShareButton from "../components/ShareButton";

export default function DealDetailPage({ onOpenAuth }) {
  const { id } = useParams();
  const { user } = useAuth();
  const [deal, setDeal] = useState(null);

  const load = async () => {
    const d = await api.get(`/deals/${id}`);
    setDeal(d.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!deal) return <div className="max-w-5xl mx-auto px-4 py-20 text-muted-foreground">Loading…</div>;

  const canSeeContacts = !!deal.participants?.[0]?.phone;
  const isSeller = user && user.phone_number === deal.seller_phone;
  const expired = deal.expires_at && new Date(deal.expires_at).getTime() < Date.now();

  const join = async () => {
    if (!user) return onOpenAuth();
    if (expired) return toast.error("This deal has expired");
    await api.post(`/deals/${id}/join`);
    toast.success("You're in the deal!");
    load();
  };

  const verify = async () => {
    if (!user) return onOpenAuth();
    try {
      await api.post(`/deals/${id}/verify`);
      toast.success("Deal verified! Participants are now visible.");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Verification failed"));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-xs text-muted-foreground">
        <Link to={productUrl({ id: deal.product_id, category: deal.product_category, product: deal.product_name, name: deal.product_name })} className="hover:underline">{deal.product_name}</Link> › Group Deal
      </div>

      <div className="mt-3 flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="t-page">{deal.product_name}</h1>
          <div className="text-muted-foreground text-sm mt-1">{categoryLabel(deal.category)} · {deal.city}</div>
          <div className="mt-3 flex items-center gap-2">
            <BhegaBadge count={deal.participant_count || 0} tone="solid" size="lg" testId="deal-bhega-badge" />
            <span className="text-xs text-muted-foreground">united in this deal</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Negotiated Offer</div>
          <div className="font-heading font-extrabold text-3xl">{inrFormat(deal.offer_price)}</div>
        </div>
      </div>

      {/* Countdown banner */}
      {deal.expires_at && (
        <div className="mt-6 p-4 bg-card text-foreground rounded-none flex items-center justify-between flex-wrap gap-3" data-testid="deal-countdown-banner">
          <CountdownTimer expiresAt={deal.expires_at} />
          <div className="text-xs text-foreground/70 max-w-md">
            Deals are time-bound. Once expired, the seller's offer is no longer valid for the group.
          </div>
        </div>
      )}

      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <div className="border border-border p-4 rounded-none">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Seller</div>
          <div className="font-semibold mt-1">{deal.seller_name}</div>
          <div className="text-sm text-muted-foreground">Contact: {deal.seller_contact_person || "—"}</div>
          <div className="text-sm flex items-center gap-1 mt-1"><Phone size={12} /> +91 {deal.seller_phone}</div>
        </div>
        <div className="border border-border p-4 rounded-none">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Leader</div>
          <div className="font-semibold mt-1 flex items-center gap-1.5"><Crown size={14} className="text-brand" />{deal.leader_name || "—"}</div>
          <div className="text-sm text-muted-foreground">{deal.participant_count} buyer{deal.participant_count !== 1 ? "s" : ""} in this group</div>
        </div>
        <div className="border border-border p-4 rounded-none">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Status</div>
          {expired ? (
            <span className="bhega-pill bg-[var(--bhega-error-soft)] text-[var(--bhega-error)] mt-2">Deal expired</span>
          ) : deal.is_verified ? (
            <span className="bhega-pill bg-[var(--bhega-info-soft)] text-[var(--bhega-info)] mt-2" data-testid="deal-verified-badge"><Verified size={11} /> Verified by seller</span>
          ) : (
            <span className="bhega-pill bg-foreground/5 text-muted-foreground mt-2">Unverified</span>
          )}
          <div className="text-xs text-muted-foreground mt-2">Created {new Date(deal.created_at).toLocaleDateString("en-IN")}</div>
        </div>
      </div>

      {deal.notes && (
        <div className="mt-4 p-4 bg-[var(--bhega-primary-surface)] border-l-4 border-[#FFBE06] rounded-none">
          <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Notes</div>
          <p className="text-sm mt-1">{deal.notes}</p>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <button onClick={join} disabled={expired} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="join-deal-btn">
          <Users size={16} /> {expired ? "Deal expired" : "Join this deal"}
        </button>
        {isSeller && !deal.is_verified && (
          <button onClick={verify} className="btn-secondary" data-testid="verify-deal-btn">
            <ShieldCheck size={16} /> Verify as seller
          </button>
        )}
        <ShareButton
          url={typeof window !== "undefined" ? window.location.href : ""}
          title={`Join ${deal.participant_count} Bhega ✊ on ${deal.product_name}`}
          subtitle={`Best price ${inrFormat(deal.offer_price)} in ${deal.city.split(",")[0]}`}
          label="Share deal"
          variant="outline"
          testId="share-deal-btn"
        />
      </div>

      {/* Participants */}
      <section className="mt-10">
        <h3 className="font-heading font-bold text-xl flex items-center gap-2">
          <span aria-hidden>✊</span> Bhega ({deal.participant_count})
        </h3>
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(deal.participants || []).map((p, i) => (
            <div key={i} className="border border-border p-3 rounded-none flex items-center gap-3" data-testid={`participant-${i}`}>
              <div className="w-9 h-9 bg-[#FFBE06] text-black font-bold rounded-full flex items-center justify-center text-sm">
                {(p.name || "U").slice(0, 1).toUpperCase()}
              </div>
              <div>
                <div className="text-sm font-semibold">{p.name || "Buyer"}</div>
                <div className="text-xs text-muted-foreground">
                  {p.phone ? `+91 ${p.phone}` : (p.phone_masked ? `+91 ${p.phone_masked}` : "Hidden until verified")}
                </div>
              </div>
            </div>
          ))}
        </div>
        {!canSeeContacts && (
          <p className="text-xs text-muted-foreground mt-3">Phone numbers unlock once the listed seller verifies this deal.</p>
        )}
      </section>

      {/* Comments */}
      <section className="mt-10">
        <CommentThread
          scope="deal"
          scopeId={deal.id}
          onOpenAuth={onOpenAuth}
          title="Deal discussion"
          emptyText="No comments yet. Be first to discuss."
          testIdPrefix="deal-"
        />
      </section>
    </div>
  );
}
